import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PayrollEntry, Employee, CompanySettings } from '../types';
import { parseLocalDateForDisplay, formatLocalDate } from './payrollPeriodCalculator';
import { supabase } from '../lib/supabase';

export const generatePayStubPDF = async (
  entry: PayrollEntry, 
  employee: Employee, 
  returnDoc: boolean = false,
  companySettings?: CompanySettings | null
) => {
  try {
    // Create new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // Format currency with 2 decimal places
    const formatCurrency = (amount: number) => amount.toFixed(2);

    // Calculate YTD totals
    const currentYear = parseLocalDateForDisplay(entry.payPeriod.end).getFullYear();
    const { data: ytdEntries } = await supabase
      .from('payroll_entries')
      .select('*')
      .eq('employee_id', employee.id)
      .gte('pay_period_end', `${currentYear}-01-01`)
      .lte('pay_period_end', entry.payPeriod.end)
      .order('pay_period_end', { ascending: true });

    const ytdTotals = ytdEntries?.reduce((acc, curr) => ({
      grossPay: acc.grossPay + curr.gross_pay,
      netPay: acc.netPay + curr.net_pay,
      taxDeductions: acc.taxDeductions + curr.tax_deduction,
      insuranceDeductions: acc.insuranceDeductions + curr.insurance_deduction,
      retirementDeductions: acc.retirementDeductions + curr.retirement_deduction,
      bonus: acc.bonus + (curr.bonus || 0),
      federalTax: acc.federalTax + curr.federal_tax,
      medicareTax: acc.medicareTax + curr.medicare_tax,
      socialSecurityTax: acc.socialSecurityTax + curr.social_security_tax,
      stateTax: acc.stateTax + curr.state_tax
    }), {
      grossPay: 0,
      netPay: 0,
      taxDeductions: 0,
      insuranceDeductions: 0,
      retirementDeductions: 0,
      bonus: 0,
      federalTax: 0,
      medicareTax: 0,
      socialSecurityTax: 0,
      stateTax: 0
    });

    // Calculate regular pay and bonus
    const regularPay = entry.grossPay - (entry.bonus || 0);
    const bonus = entry.bonus || 0;

    // Add company header
    if (companySettings) {
      if (companySettings.displayLogo && companySettings.logoUrl) {
        try {
          const response = await fetch(companySettings.logoUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          
          await new Promise((resolve, reject) => {
            reader.onload = resolve;
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const logoData = reader.result as string;
          doc.addImage(logoData, 'PNG', pageWidth / 2 - 15, 10, 30, 30, undefined, 'FAST');
          doc.setFontSize(10);
          doc.text('Pay Stub', pageWidth / 2, 48, { align: 'center' });
        } catch (error) {
          console.error('Error loading logo:', error);
          // Fallback to text if logo fails to load
          doc.setFontSize(14);
          doc.text(companySettings.companyName, pageWidth / 2, 15, { align: 'center' });
          doc.setFontSize(10);
          doc.text('Pay Stub', pageWidth / 2, 22, { align: 'center' });
        }
      } else {
        doc.setFontSize(14);
        doc.text(companySettings.companyName, pageWidth / 2, 15, { align: 'center' });
        doc.setFontSize(10);
        doc.text('Pay Stub', pageWidth / 2, 22, { align: 'center' });
      }

      // Add company contact info
      doc.setFontSize(8);
      doc.text(companySettings.companyAddress, pageWidth / 2, companySettings.displayLogo ? 55 : 29, { align: 'center' });
      doc.text(companySettings.companyPhone, pageWidth / 2, companySettings.displayLogo ? 60 : 34, { align: 'center' });
    } else {
      doc.setFontSize(14);
      doc.text('PayrollPro', pageWidth / 2, 12, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Pay Stub', pageWidth / 2, 18, { align: 'center' });
    }
    
    doc.text(
      `Pay Period: ${formatLocalDate(entry.payPeriod.start)} - ${formatLocalDate(entry.payPeriod.end)}`,
      pageWidth / 2,
      companySettings?.displayLogo ? 65 : 40,
      { align: 'center' }
    );

    // Add Pay Date if available
    if (entry.payDate) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(
        `Pay Date: ${formatLocalDate(entry.payDate)}`,
        pageWidth / 2,
        companySettings?.displayLogo ? 71 : 46,
        { align: 'center' }
      );
      doc.setFont(undefined, 'normal');
    }

    // Employee Information - more compact layout
    autoTable(doc, {
      startY: entry.payDate ? (companySettings?.displayLogo ? 76 : 51) : (companySettings?.displayLogo ? 70 : 45),
      head: [['Employee Information', 'Payment Information']],
      body: [
        [`Name: ${employee.firstName} ${employee.lastName}`, `Rate: $${employee.salary.toFixed(2)}${employee.paymentType === 'hourly' ? '/hr' : '/year'}`],
        [`ID: ${employee.id.split('-')[0]}`, `Type: ${employee.paymentType} | Status: ${employee.taxStatus}`],
        [`Position: ${employee.position}`, `Department: ${employee.department}`]
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' }
      }
    });

    // Earnings and Deductions - streamlined layout
    const finalY = (doc as any).lastAutoTable.finalY + 5;
    
    autoTable(doc, {
      startY: finalY,
      head: [['Earnings & Deductions', 'Current', 'YTD']],
      body: [
        ['Regular Pay', `$${formatCurrency(regularPay)}`, `$${formatCurrency(ytdTotals ? ytdTotals.grossPay - ytdTotals.bonus : 0)}`],
        ...(bonus > 0 ? [['Bonus', `$${formatCurrency(bonus)}`, `$${formatCurrency(ytdTotals?.bonus || 0)}`]] : []),
        ['Total Gross Pay', `$${formatCurrency(entry.grossPay)}`, `$${formatCurrency(ytdTotals?.grossPay || 0)}`],
        ['', '', ''],
        ['Tax Deductions:', '', ''],
        ['Federal Tax', `$${formatCurrency(entry.deductions.federalTax)}`, `$${formatCurrency(ytdTotals?.federalTax || 0)}`],
        ['Social Security', `$${formatCurrency(entry.deductions.socialSecurityTax)}`, `$${formatCurrency(ytdTotals?.socialSecurityTax || 0)}`],
        ['Medicare', `$${formatCurrency(entry.deductions.medicareTax)}`, `$${formatCurrency(ytdTotals?.medicareTax || 0)}`],
        ['State Tax', `$${formatCurrency(entry.deductions.stateTax)}`, `$${formatCurrency(ytdTotals?.stateTax || 0)}`],
        ['Total Tax', `$${formatCurrency(entry.deductions.tax)}`, `$${formatCurrency(ytdTotals?.taxDeductions || 0)}`],
        ['', '', ''],
        ['Other Deductions:', '', ''],
        ['Insurance', `$${formatCurrency(entry.deductions.insurance)}`, `$${formatCurrency(ytdTotals?.insuranceDeductions || 0)}`],
        ['Retirement', `$${formatCurrency(entry.deductions.retirement)}`, `$${formatCurrency(ytdTotals?.retirementDeductions || 0)}`],
        ['Total Other', `$${formatCurrency(
          entry.deductions.insurance + entry.deductions.retirement
        )}`, `$${formatCurrency((ytdTotals?.insuranceDeductions || 0) + (ytdTotals?.retirementDeductions || 0))}`],
        ['', '', ''],
        ['Total Deductions', `$${formatCurrency(
          entry.deductions.tax +
          entry.deductions.insurance +
          entry.deductions.retirement
        )}`, `$${formatCurrency(
          (ytdTotals?.taxDeductions || 0) +
          (ytdTotals?.insuranceDeductions || 0) +
          (ytdTotals?.retirementDeductions || 0)
        )}`],
        ['Net Pay', `$${formatCurrency(entry.netPay)}`, `$${formatCurrency(ytdTotals?.netPay || 0)}`]
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 35, halign: 'right' },
        2: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: 15, right: 15 },
      didParseCell: function(data) {
        // Style the section headers
        if (data.cell.text[0].endsWith(':')) {
          data.cell.styles.fontStyle = 'bold';
        }
        // Style the totals
        if (
          data.cell.text[0] === 'Total Gross Pay' ||
          data.cell.text[0] === 'Total Tax' ||
          data.cell.text[0] === 'Total Other' ||
          data.cell.text[0] === 'Total Deductions' ||
          data.cell.text[0] === 'Net Pay'
        ) {
          data.cell.styles.fontStyle = 'bold';
        }
        // Style the bonus
        if (data.cell.text[0] === 'Bonus') {
          data.cell.styles.textColor = [22, 163, 74];
        }
      }
    });

    // Add footer - minimal spacing
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(7);
    doc.text(
      'This is an official pay stub document.',
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );

    if (returnDoc) {
      return doc;
    }

    // Save the PDF
    doc.save(`paystub-${employee.lastName}-${entry.payPeriod.end}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};